import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
} from "@mui/material";
import axios from "axios";
import Header from "components/Header";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const AboutUs = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const [imagePath1, setImagePath1] = useState("");
  const [imagePath2, setImagePath2] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [milestones, setMilestones] = useState([{ title: "", description: "", image: "" }]);
  const [founder, setFounder] = useState({ title: "", subheading: "", image: "" });
  const [openModal, setOpenModal] = useState(false);
  const [currentService, setCurrentService] = useState(null); // For update functionality
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${apiUrl}/aboutus/getaboutus`);
      setData(response.data);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setIsLoading(false);
    }
  };

  const handleImageUpload = async (file, setImagePath) => {
    const formData = new FormData();
    formData.append("image", file);

    try {
      const response = await axios.post(`${apiUrl}/upload`, formData);
      console.log("this is image path>>:", response.data.filePath);
      // setImagePath(response.data.filePath); // Assuming the backend returns the image path
      setImagePath(response.data.url);
    } catch (error) {
      console.error("Error uploading image:", error);
      setErrorMessage("Error uploading image.");
      setSnackbarOpen(true);
    }
  };

  const handleFileChangeMilestone = (index, event) => {
    const selectedImage = event.target.files[0];
    if (selectedImage) {
      handleImageUpload(selectedImage, (filePath) => {
        const updatedMilestones = [...milestones];
        updatedMilestones[index].image = filePath; // Store the image path for the milestone
        setMilestones(updatedMilestones);
      });
    }
  };

  const handleFileChangeFounder = (event) => {
    const selectedImage = event.target.files[0];
    if (selectedImage) {
      handleImageUpload(selectedImage, setImagePath2); // Automatically upload the image
    }
  };

  const handleAddMilestone = () => {
    setMilestones([...milestones, { title: "", description: "", image: "" }]);
  };

  const handleRemoveMilestone = (index) => {
    const newMilestones = milestones.filter((_, i) => i !== index);
    setMilestones(newMilestones);
  };

  const handleMilestoneChange = (index, field, value) => {
    const newMilestones = milestones.map((milestone, i) =>
      i === index ? { ...milestone, [field]: value } : milestone
    );
    setMilestones(newMilestones);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleFounderChange = (field, value) => {
    setFounder({ ...founder, [field]: value });
  };

  const validateForm = () => {
    // Validate the main fields
    if (!title.trim() || !description.trim()) {
      setErrorMessage("Title and description are required.");
      setSnackbarOpen(true);
      return false;
    }
  
    // Validate milestones
    for (let milestone of milestones) {
      if (!milestone.title.trim() || !milestone.description.trim()) {
        setErrorMessage("Each milestone must have a title and description.");
        setSnackbarOpen(true);
        return false;
      }
    }
  
    // Validate founder fields
    if (!founder.title.trim() || !founder.subheading.trim() || !imagePath2) {
      setErrorMessage("Please fill all the required fields for the founder.");
      setSnackbarOpen(true);
      return false;
    }
  
    return true; // All validations passed
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) {
      setErrorMessage("Please fill all the required fields.");
      setSnackbarOpen(true);
      return;
    }
    const postData = {
      title,
      description,
      featured: milestones.map(milestone => ({
        ...milestone,
        image: milestone.image, // Using the image path from the milestone
      })),
      founder: {
        ...founder,
        image: imagePath2, // Set founder image path
      },
    };

    try {
      const response = await axios.post(`${apiUrl}/aboutus/createaboutus`, postData);
      setData((prevData) => [...prevData, response.data]);
      handleCloseModal();
      resetForm();
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const handleOpenUpdateModal = (service) => {
    setCurrentService(service);
    setTitle(service.title);
    setDescription(service.description);
    setMilestones(service.featured);
    setFounder(service.founder);
    setImagePath2(service.founder.image); // Set imagePath2 for founder
    setOpenModal(true);
  };

  const handleUpdate = async (event) => {
    event.preventDefault();
    if (!validateForm()) {
      alert("Please fill all the required fields.");
      return;
    }
    const updateData = {
      title,
      description,
      featured: milestones.map(milestone => ({
        ...milestone,
        image: milestone.image, // Include the milestone image paths
      })),
      founder: {
        ...founder,
        image: imagePath2, // Set updated founder image path
      },
    };

    try {
      const response = await axios.put(`${apiUrl}/aboutus/updateaboutus/${currentService._id}`, updateData);
      setData((prevData) =>
        prevData.map((item) => (item._id === currentService._id ? response.data : item))
      );
      handleCloseModal();
      resetForm();
    } catch (error) {
      console.error("Error updating service:", error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${apiUrl}/aboutus/deleteaboutus/${id}`);
      setData((prevData) => prevData.filter((item) => item._id !== id));
    } catch (error) {
      console.error("Error deleting service:", error);
    }
  };

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setMilestones([{ title: "", description: "", image: "" }]);
    setFounder({ title: "", subheading: "", image: "" });
    setImagePath1(""); // Reset imagePath1
    setImagePath2(""); // Reset imagePath2
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    resetForm();
    setCurrentService(null);
  };

  return (
    <Box m="1.5rem 2.5rem">
      <Header title="ABOUT US" subtitle="Create About Us Page" />
      <Button variant="contained" color="primary" onClick={() => setOpenModal(true)}>
        Add About Us Entry
      </Button>

      <Dialog open={openModal} onClose={handleCloseModal}>
        <DialogTitle>{currentService ? "Update Entry" : "Add New Entry"}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Title"
            type="text"
            fullWidth
            variant="outlined"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Description"
            type="text"
            fullWidth
            variant="outlined"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          
          <Typography variant="h6">Featured Milestones</Typography>
          {milestones.map((milestone, index) => (
            <Box key={index} mb={2}>
              <TextField
                margin="dense"
                label="Milestone Title"
                type="text"
                fullWidth
                variant="outlined"
                value={milestone.title}
                onChange={(e) => handleMilestoneChange(index, "title", e.target.value)}
              />
              <TextField
                margin="dense"
                label="Milestone Description"
                type="text"
                fullWidth
                variant="outlined"
                value={milestone.description}
                onChange={(e) => handleMilestoneChange(index, "description", e.target.value)}
              />
              {/* Updated to use file upload */}
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUploadIcon />}
              >
                Upload Image
                <input
                  type="file"
                  hidden
                  onChange={(e) => handleFileChangeMilestone(index, e)} // Pass the index for each milestone
                />
              </Button>
              {milestone.image && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <img src={milestone.image} alt="image" width="50" height="50" />
                </Typography>
              )}
              <Button color="secondary" onClick={() => handleRemoveMilestone(index)}>Remove Milestone</Button>
            </Box>
          ))}
          <Button variant="contained" color="primary" onClick={handleAddMilestone}>
            Add Milestone
          </Button>

          <Typography variant="h6" mt={2}>Founder Information</Typography>
          <TextField
            margin="dense"
            label="Founder Title"
            type="text"
            fullWidth
            variant="outlined"
            value={founder.title}
            onChange={(e) => handleFounderChange("title", e.target.value)}
          />
          <TextField
            margin="dense"
            label="Founder Subheading"
            type="text"
            fullWidth
            variant="outlined"
            value={founder.subheading}
            onChange={(e) => handleFounderChange("subheading", e.target.value)}
          />
          {/* Updated to use file upload for founder image */}
          <Button
            variant="outlined"
            component="label"
            startIcon={<CloudUploadIcon />}
          >
            Upload Founder Image
            <input
              type="file"
              hidden
              onChange={handleFileChangeFounder} // Store the file object instead
            />
          </Button>
          {imagePath2 && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              <img src={imagePath2}  alt="image" width="50" height="50" />
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="primary">
            Cancel
          </Button>
          <Button onClick={currentService ? handleUpdate : handleSubmit} color="primary">
            {currentService ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      <Box mt={4}>
        <Typography variant="h6">Existing Entries:</Typography>
        <Box mt={2}>
          {data.map((item) => (
            <Box key={item._id} p={2} border="1px solid" borderRadius="5px" mb={2}>
              <Typography variant="h6">{item.title}</Typography>
              <Typography>{item.description}</Typography>
              <Typography variant="subtitle1">Founder: {item.founder.title}</Typography>
              <Button variant="contained"  style={{marginRight: 8}} color="primary" onClick={() => handleOpenUpdateModal(item)}>
                Update
              </Button>
              <Button variant="contained" color="secondary" onClick={() => handleDelete(item._id)}>
                Delete
              </Button>
            </Box>
          ))}
        </Box>
      </Box>
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AboutUs;
